// BETA-ONLY: Remove this feature when beta ends

import { useState } from 'react';
import { Loader2, ClipboardList, ArrowRight } from 'lucide-react';
import { QuestionSection } from './QuestionSection';
import { SingleSelectGroup } from './SingleSelectGroup';
import { MultiSelectGroup } from './MultiSelectGroup';
import { useSubmitBetaProfile } from '../hooks/useBetaProfile';
import {
  MONTHLY_ADMIN_TIME_OPTIONS,
  PAIN_POINT_OPTIONS,
  CURRENT_TOOL_OPTIONS,
  TOP_OUTCOME_OPTIONS,
  REFERRAL_SOURCE_OPTIONS,
  FEEDBACK_COMFORT_OPTIONS,
} from '../types/betaProfile';

interface BetaProfileQuestionnaireProps {
  onComplete: () => void;
  onBack: () => void;
}

export function BetaProfileQuestionnaire({
  onComplete,
  onBack,
}: BetaProfileQuestionnaireProps): JSX.Element {
  // Form state
  const [monthlyAdminTime, setMonthlyAdminTime] = useState<string | null>(null);
  const [painPoints, setPainPoints] = useState<string[]>([]);
  const [currentTools, setCurrentTools] = useState<string[]>([]);
  const [topOutcome, setTopOutcome] = useState<string | null>(null);
  const [referralSource, setReferralSource] = useState<string | null>(null);
  const [feedbackComfort, setFeedbackComfort] = useState<string | null>(null);

  const { submit, loading, error } = useSubmitBetaProfile();

  const canSubmit =
    monthlyAdminTime !== null &&
    painPoints.length > 0 &&
    currentTools.length > 0 &&
    topOutcome !== null &&
    referralSource !== null &&
    feedbackComfort !== null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const result = await submit({
        monthlyAdminTime: monthlyAdminTime!,
        painPoints,
        currentTools,
        topOutcome: topOutcome!,
        referralSource: referralSource!,
        feedbackComfort: feedbackComfort!,
      });

      if (result.success) {
        onComplete();
      }
    } catch {
      // Error is already set by the hook
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <ClipboardList className="w-8 h-8 text-evergreen" />
        </div>
        <h2 className="font-display text-2xl text-deep-forest mb-2">
          Tell us about your caregiving
        </h2>
        <p className="text-muted">
          This helps us understand your needs and build better tools. Takes about 1 minute.
        </p>
      </div>

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-error mb-6">{error}</div>
            )}

            {/* Q1: Monthly admin time */}
            <QuestionSection
              number={1}
              title="How much time do you spend on care admin each month?"
              subtitle="Paperwork, phone calls, insurance, scheduling, etc."
              isActive={true}
            >
              <SingleSelectGroup
                options={MONTHLY_ADMIN_TIME_OPTIONS}
                value={monthlyAdminTime}
                onChange={setMonthlyAdminTime}
                columns={3}
              />
            </QuestionSection>

            {/* Q2: Pain points */}
            <QuestionSection
              number={2}
              title="Which of these have you experienced in the last 6 months?"
              subtitle="Select all that apply"
              isActive={monthlyAdminTime !== null}
            >
              <MultiSelectGroup
                options={PAIN_POINT_OPTIONS}
                values={painPoints}
                onChange={setPainPoints}
                exclusiveValue="none"
                columns={2}
              />
            </QuestionSection>

            {/* Q3: Current tools */}
            <QuestionSection
              number={3}
              title="What do you currently use to track care information?"
              subtitle="Select all that apply"
              isActive={painPoints.length > 0}
            >
              <MultiSelectGroup
                options={CURRENT_TOOL_OPTIONS}
                values={currentTools}
                onChange={setCurrentTools}
                columns={2}
              />
            </QuestionSection>

            {/* Q4: Top outcome */}
            <QuestionSection
              number={4}
              title="Which outcome matters most to you?"
              isActive={currentTools.length > 0}
            >
              <SingleSelectGroup
                options={TOP_OUTCOME_OPTIONS}
                value={topOutcome}
                onChange={setTopOutcome}
                columns={1}
              />
            </QuestionSection>

            {/* Q5: Referral source */}
            <QuestionSection
              number={5}
              title="How did you hear about Hesvara?"
              isActive={topOutcome !== null}
            >
              <SingleSelectGroup
                options={REFERRAL_SOURCE_OPTIONS}
                value={referralSource}
                onChange={setReferralSource}
                columns={2}
              />
            </QuestionSection>

            {/* Q6: Feedback comfort */}
            <QuestionSection
              number={6}
              title="Are you comfortable providing detailed feedback during beta?"
              isActive={referralSource !== null}
            >
              <SingleSelectGroup
                options={FEEDBACK_COMFORT_OPTIONS}
                value={feedbackComfort}
                onChange={setFeedbackComfort}
                columns={1}
              />
            </QuestionSection>

            {/* Actions */}
            <div className="flex gap-4 pt-6 border-t border-soft-neutral">
              <button
                type="button"
                onClick={onBack}
                disabled={loading}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!canSubmit || loading}
                className={`btn-primary flex-1 flex items-center justify-center gap-2 ${
                  !canSubmit ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Continue <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
